import { useEffect, useRef, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import { blue, red, grey, green} from "@mui/material/colors";
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import SettingsIcon from '@mui/icons-material/Settings';
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isEqual, isNull, isUndefined, size, replace, isNil } from "lodash";

import appConfigService from "@plannel/services/system/app-config-service";
import targetInvSimulationService from "@plannel/services/ip/target-inv-simulation-service";
import classificationService from "@plannel/services/ip/classification-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import { BulkValueUpdateButton, ResultButton, CreateButton, RemoveButton, SaveButton, RunButton, RunningButton } from "@plannel/components/ActionIconButton";
import IpBulkValueUpdateModal from "@plannel/pages/inventory-plan/ip-modal/IpBulkValueUpdateModal";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import NumberUtil from "@plannel/utils/number-util";

import CreateScenarioModal from "./CreateScenarioModal";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { reloadIpVersionFilter } from "@plannel/components/filter/IpVersionFilter";
import dateUtils from "@plannel/utils/date-util";
import GridDatePicker from "@plannel/components/GridDatePicker";
import { IpTargetLocationItemHrchyComponentContent, IpTargetLocationItemHrchyEnabledComponent } from "../ip-config/IpSettings";
import ClassificationSettingsModal from "@plannel/components/modal/ClassificationSettingsModal";
import { FS } from "@plannel/utils/font-util";

const CURRENT_DATE = new ZDate();
const MAX_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 0);
const MIN_DATE = new ZDate(MAX_DATE.getFullYear(), MAX_DATE.getMonth() + 1, 1);
const SCENARIO_GRID_ID = "SCENARIO_GRID_ID";
const TARGET_INV_POLICY_GRID_ID = "TAREGT_INV_POLICY_GRID_ID";
const POLICY_TITLE = "targetInvSimulation";
const DIALOG_TITLE = { ADD: "ADD", SAVE: "SAVE", DELETE: "DELETE", RUN: "RUN_SIMULATION" };
const INV_POLICY_TYPE = CodeUtil.ipPolicyCd;
const CALC_TYPE = CodeUtil.calcType;
const { FORECAST_GAP } = CALC_TYPE;

const invPolicyTypes = [
  INV_POLICY_TYPE.SD,
  INV_POLICY_TYPE.MAX_AVG,
  INV_POLICY_TYPE.NOR_DIST,
  INV_POLICY_TYPE.NOR_DIST_SUP
];

const {
  SIM_TARGET_FILTER_POLICY,
  SIM_TARGET_FILTER_ENABLED,
  TARGET_LOCATION, 
  LOCATION_NODE_TREE,
  TARGET_ITEM_HRCHY,
  ITEM_HRCHY_NODE_TREE
} = CodeUtil.ipSettings;

const DELETE = "DELETE";
const { READY, PLANNED, RELEASED, RUNNING } = CodeUtil.versionStatusCode;
const { snackBarMessageColor } = ColorUtil;
const { module } = CodeUtil;
const { IP } = module;

const TargetSimulationSettingsComponent = (props) => {
  const { setTargetSimulationModalOpen, setModalSelectedScenario, data, setSimTargetFilterPolicy, setFilterValue } = props;
  const isEnabled = data?.policyObj?.[SIM_TARGET_FILTER_POLICY]?.[SIM_TARGET_FILTER_ENABLED] || false;

  const buttonHandler = () => {
    setTargetSimulationModalOpen(true);

    const policyObj = data?.policyObj;
    const selectedSimTargetPolicy = policyObj?.[SIM_TARGET_FILTER_POLICY] || { [SIM_TARGET_FILTER_ENABLED]: false, [TARGET_LOCATION]: [], [LOCATION_NODE_TREE]: [], [TARGET_ITEM_HRCHY]: [], [ITEM_HRCHY_NODE_TREE]: [] };
    const selectedTargetLocTree = selectedSimTargetPolicy?.[LOCATION_NODE_TREE] || [];
    const selectedTargetItemHrchyTree = selectedSimTargetPolicy?.[ITEM_HRCHY_NODE_TREE] || [];

    setSimTargetFilterPolicy(selectedSimTargetPolicy);
    setFilterValue({ locations: selectedTargetLocTree, category: selectedTargetItemHrchyTree });

    setModalSelectedScenario(data);
  }

  return (
    <Box textAlign={"center"}>
      <IconButton aria-label={"target-sim-compo"} color={isEnabled ? "info" : "default"} size="small" sx={{ fontSize: FS.DISPLAY }}
        onClick={buttonHandler}
      >
        <SettingsIcon fontSize="inherit" />
      </IconButton>
    </Box>
  )
}

const TargetInvSimulation = ({ t, viewName, title }) => {
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [isRunning, setIsRunning] = useState(false);
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [targetSimulationModalOpen, setTargetSimulationModalOpen] = useState(false);
  const [updateTargets, setUpdateTargets] = useState(["safetyStockFlg", "cycleInvFlg", "salesFlg", "shipmentsFlg", "invPolicyCd", "invPolicyVal"]);

  const [filterValue, setFilterValue] = useState({locations: [], category: []});
  const [simTargetFilterPolicy, setSimTargetFilterPolicy] = useState({ [SIM_TARGET_FILTER_ENABLED]: false, [TARGET_LOCATION]: [], [LOCATION_NODE_TREE]: [], [TARGET_ITEM_HRCHY]: [], [ITEM_HRCHY_NODE_TREE]: [] });

  const [massUpdateModalOpen, setMassUpdateModalOpen] = useState(false);

  // for scenario
  const [scenarios, setScenarios] = useState([]);
  /* orgScenarios는 result에 영향을 주지 않는 항목들을 업데이트하는 경우 결과값을 지울 필요가 없기때문에 필요합니다. */
  const [orgScenarios, setOrgScenarios] = useState([]);

  const [classificationList, setClassificationList] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState({});
  const [modalSelectedScenario, setModalSelectedScenario] = useState({});

  const [createNewModalOpen, setCreateNewModalOpen] = useState(false);
  // scenario search value
  const searchScenarioName = useRef(reduxViewState?.searchCode ? reduxViewState.searchCode : "");

  const [gridLoading, setGridLoading] = useState(false);
  const scenarioGridRef = useRef();
  const targetInventoryPolicyGridRef = useRef();

  const [targetInventoryPolicy, setTargetInventoryPolicy] = useState([]);
  const [totalRowData, setTotalRowData] = useState({});
  const [planVersions, setPlanVersions] = useState([]);

  const selectedRowRef = useRef({});
  const currencyMap = useRef({});
  const userData = JSON.parse(localStorage.getItem("user"));
  const defaultMainGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: SCENARIO_GRID_ID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultPolicyGridMemo = useMemo(() => {
    return DefaultGridSetting({ title: POLICY_TITLE, viewName, gridId: TARGET_INV_POLICY_GRID_ID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showResultsPage = () => {
    const reduxKey = "TARGET_INV_SIMULATION_RESULTS";
    const ipVersion = { ...selectedScenario };
    const objectViewState = reduxUtil.createObjectState(reduxKey, { ipVersion, currentPage: 0, pageSize: 100 });
    
    reduxDispatch(reduxUtil.updateViewState(objectViewState));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  }

  const getRequestParams = () => {
    const params = {};
    params.orderByColumn = "updatedTs";
    params.sortType = "desc";

    return params;
  };

  const iconColor = planVersion => {
    if (planVersion) {
      if (isEqual(planVersion.statusCd, RELEASED)) {
        return grey[900];
      }

      return isEmpty(planVersion.execTs) ? red[600] : green[600];
    }

    return null;
  }

  const sortPlanVersions = versions => {
    const basicVersions = [];
    const executedVersions = [];
    const releaseVersions = [];

    for (const ver of versions) {
      const { statusCd, execTs } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else if(!isEqual(statusCd, DELETE)){
        if (isNull(execTs)) {
          basicVersions.push(ver);
        } else {
          executedVersions.push(ver);
        }
      }
    }

    return [...basicVersions, ...executedVersions, ...releaseVersions];
  }

  const retrieveIpVersions = () => {
    planVersionService.getAll({ moduleCd: IP, orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        setPlanVersions(sortPlanVersions(results));
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveClassificationList = () => {
    classificationService.getAll({ statusCd: PLANNED, orderByColumn: "updatedTs", sortType: "desc" }) // PLANNED로 조회하더도 READY 까지 조회되도록 Backend 처리.
    .then(response => {
      const { results } = response.data;
      if (isEmpty(results)) {
        setClassificationList([]);
      } else {
        setClassificationList(results);
      }
    })
    .catch(e => {
      debug(e);
    })
  }

  const cellPercentValueFormatter = (param) => {
    const postFix = "%";
    return param.value + postFix;
  }

  // 계획 버전 상태 → 채움-틴트 칩 (버전 드롭다운/분류 모달 배지와 동일 규격)
  const statusCellRenderer = (params) => {
    const statusCd = params.value;
    if (isNull(statusCd) || isUndefined(statusCd) || statusCd === "") return statusCd ?? "";
    const label = CodeUtil.versionStatusLabel(statusCd);
    return <Chip size="small" label={label} sx={{ height: 22, fontSize: FS.BODY, ...ColorUtil.tintChipSx(ColorUtil.versionStatusColor(statusCd)) }} />;
  }

  const percentValueCellRenderer = (params) => {
    let arrow = "";

    if (params.column.getColId() === "xClass" || params.column.getColId() === "yClass") {
      arrow = <ArrowDownward color={blue[600]} fontSize="1.5rem" />;
    } else if (params.column.getColId() === "zClass") {
      arrow = <ArrowUpward color={red[600]} fontSize="1.5rem" />;
    }

    if (!isNull(params.value) && !isUndefined(params.value)) {
      return <div>{cellPercentValueFormatter(params)}{arrow}</div>;
    }

    return params.value;
  }

  const classificationFormatter = (params) => {
    return params.value?.name ? params.value.name : "";
  }

  const classificationValueParser = (params) => {
    return params?.newValue;
  }

  const salesDemandTypeEditableHandler = (selectedScenario, params) => {
    const editableByStatus = isNotReleaseVersion({ data: selectedScenario });
    if (!editableByStatus) {
      return false;
    }

    const classificationObj = selectedScenario?.classificationDto;
    const isPlantLocAnalysis = classificationObj?.plantLocAnalysis === true;
    if (!isPlantLocAnalysis) {
      return true;
    }

    const { data } = params;
    const locationObject = data?.location;
    if (!isNil(locationObject)) {
      const { locType } = locationObject;
      if (locType === CodeUtil.locationType.PLANT.cd) {
        return false;
      }
    }
    
    return true;
  }

  const plantLocEditableCellStyle = (selectedScenario, params) => {
    const style = {};

    const classificationObj = selectedScenario?.classificationDto;
    const isPlantLocAnalysis = classificationObj?.plantLocAnalysis === true;
    if (!isPlantLocAnalysis) {
      return style;
    }

    const { data } = params;
    const locationObject = data?.location;
    if (!isNil(locationObject)) {
      const { locType } = locationObject;
      if (locType === CodeUtil.locationType.PLANT.cd) {
        style.backgroundColor = grey[300];
      }
    }

    return style;
  }

  const isNotReleaseVersion = ({ data }) => {
    const { statusCd } = data;

    return !(isUndefined(statusCd) || isNull(statusCd) || isEqual(statusCd, RELEASED));
  }

  const demandPlanEditableHandler = (parmas) => {
    const { data } = parmas;

    const classificationObj = data?.classificationDto;
    /* XYZ 계산 방식이 FORECAST_GAP인 경우 Demand 설정 불가. */
    if (isEqual(classificationObj?.xyzCalcType, FORECAST_GAP)) {
      return false;
    }

    /* PLANT-LOC 분석인 경우 Demand 설정 불가. */
    if (classificationObj?.plantLocAnalysis === true) {
      return false;
    }

    /* RELEASED 버전은 Demand 설정 불가. */
    return isNotReleaseVersion(parmas);
  }

  const forecastGapEditableHandler = (params) => {
    const { data } = params;

    const classificationObj = data?.classificationDto;
    /* XYZ 계산 방식이 FORECAST_GAP인 경우 Demand 설정 불가. */
    if (isEqual(classificationObj?.xyzCalcType, FORECAST_GAP)) {
      return false;
    }

    return isNotReleaseVersion(params);
  }

  const demandPlanCellStyle = (params, e) => {
    const { data } = params;

    const style = {};
    const classificationObj = data?.classificationDto;
    /* XYZ 계산 방식이 FORECAST_GAP인 경우 Demand 셀 스타일 변경. */
    /* PLANT-LOC 분석인 경우 Demand 셀 스타일 변경. */
    if (isEqual(classificationObj?.xyzCalcType, FORECAST_GAP) || classificationObj?.plantLocAnalysis) {
      style.backgroundColor = grey[300];
    }

    /* Footer */
    if (!isNil(e) && isEqual(e.node.rowPinned, 'bottom')) {
      return {};
    }

    return style;
  }

  const forecastGapCellStyle = (params, e) => {
    const { data } = params;

    const style = {};
    if (isEqual(data?.classificationDto?.xyzCalcType, FORECAST_GAP)) {
      style.backgroundColor = grey[300];
    }

    /* Footer */
    if (!isNil(e) && isEqual(e.node.rowPinned, 'bottom')) {
      return {};
    }

    return style;
  }

  const scenariosGridColDefs = [
    { headerName: "id", field: "id", type: ["nonEditableColumn", "defaultValueFormatter"], width: 0, hide: true, suppressColumnsToolPanel: true },
    { headerName: "scenario", field: "name", width: 300, pinned: "left", lockPinned: true, editable: isNotReleaseVersion },
    { headerName: "descTxt", field: "descTxt", width: 250, pinned: "left", lockPinned: true }, //  cellStyle: cellDefaultStyle
    {
      headerName: "advancedSettings", field: "advancedSettings", width: 120, pinned: "left", lockPinned: true,
      cellRenderer: TargetSimulationSettingsComponent,
      cellRendererParams: { setTargetSimulationModalOpen, setModalSelectedScenario, setSimTargetFilterPolicy, setFilterValue }
    },
    { headerName: "status", field: "statusCd", width: 140, type: ["nonEditableColumn"], pinned: "left", lockPinned: true, cellRenderer: statusCellRenderer },
    { headerName: "lastReleasedVersion", field: "applyFlg", width: 130, type: ["nonEditableColumn", "booleanColumn"], pinned: "left", lockPinned: true },
    {
      headerName: "classificationName", field: "classificationDto",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["nonEditableColumn"],
      sortingOrder: [null],
      valueFormatter: classificationFormatter,
      valueParser: classificationValueParser,
      cellEditorParams: {
        width: 300,
        valueFormatter: classificationFormatter,
        values: classificationList
      },
      cellRenderer: ClassificationSettingsModal,
      cellRendererParams: { isCellRenderer: true },
      editable: isNotReleaseVersion,
      width: 300
    },
    {
      headerName: "actSalesShipment", groupId: "actualSalesGrp",
      children: [
        {
          headerName: "numOfMonths",
          field: "actualPeriodVal", 
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo,
            min: 0,
            max: 120,
          },
          type: ["numberColumn", "rightAligned"],
          editable: isNotReleaseVersion,
          width: 110
        },
        {
          headerName: "periodEnd",
          field: "actualPeriodEndDate",
          type: ["defaultValueParser"],
          cellEditor: GridDatePicker,
          cellEditorParams: { dateType: "month", maxDate: MAX_DATE},
          valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM"),
          editable: isNotReleaseVersion,
          width: 125
        },
      ]
    },
    {
      headerName: "demandPlan", groupId: "demandPlanGrp",
      children: [
        {
          headerName: "periodStart",
          field: "demandPeriodStartDate",
          type: ["defaultValueParser"],
          cellEditor: GridDatePicker,
          cellEditorParams: { dateType: "month", minDate: MIN_DATE, isFirstDay: true },
          valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM"),
          editable: demandPlanEditableHandler,
          cellStyle: demandPlanCellStyle,
          width: 125
        },
        {
          headerName: "numOfMonths",
          field: "demandPeriodVal",
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo,
            min: 0,
            max: 120,
          },
          type: ["numberColumn", "rightAligned"],
          editable: demandPlanEditableHandler,
          cellStyle: demandPlanCellStyle,
          width: 110
        }
      ]
    },
    {
      headerName: "abcXyz", groupId: "abcXyzGrp",
      children: [
        {
          headerName: "A", field: "aClass", cellRenderer: percentValueCellRenderer,
          type: ["nonEditableColumn", "rightAligned"], width: 80
        },
        {
          headerName: "B", field: "bClass", cellRenderer: percentValueCellRenderer,
          type: ["nonEditableColumn", "rightAligned"], width: 80
        },
        {
          headerName: "C", field: "cClass", cellRenderer: percentValueCellRenderer,
          type: ["nonEditableColumn", "rightAligned"], width: 80
        },
        {
          headerName: "X", field: "xClass", cellRenderer: percentValueCellRenderer,
          type: ["nonEditableColumn", "rightAligned"], width: 80
        },
        {
          headerName: "Y", field: "yClass", cellRenderer: percentValueCellRenderer,
          type: ["nonEditableColumn", "rightAligned"], width: 80
        },
        {
          headerName: "Z", field: "zClass", cellRenderer: percentValueCellRenderer,
          type: ["nonEditableColumn", "rightAligned"], width: 80
        }]
    }
  ];

  const valueFormatter = params => {
    return GridUtils.gridValueL10N(params.value);
  }

  const cellEditorSelector = (params) => {
    return {
      component: NumericEditor,
      params: cellEditorParams(params)
    }
  }

  const cellDynamicStyle = (params) => {
    const styles = {};
    if (params.column.getColId() === "invGapAmt") {
      if (params.value > 0) {
        styles.color = red[500];
      } else if (params.value < 0) {
        styles.color = blue[500];
      }
    } else {
      if (params.data) {
        const { invPolicyCd } = params.data;

        if (invPolicyCd === INV_POLICY_TYPE.MAX_AVG) {
          styles.color = grey[400];
        }
      }
    }

    return styles;
  }

  const invPolicyValCellRenderer = (params) => {
    if (params.data) {
      const { invPolicyCd } = params.data;

      const percentPostFix = "%";
      const dayPostFix = "days";

      if (invPolicyCd === INV_POLICY_TYPE.NOR_DIST || invPolicyCd === INV_POLICY_TYPE.NOR_DIST_SUP) {
        return params.value + percentPostFix;
      }

      if (invPolicyCd === INV_POLICY_TYPE.SD) {
        return params.value + dayPostFix;
      }
    }

    return params.value;
  }

  const cellEditorParams = (params) => {
    if (params.data) {
      const { invPolicyCd } = params.data;

      if (invPolicyCd === INV_POLICY_TYPE.NOR_DIST || invPolicyCd === INV_POLICY_TYPE.NOR_DIST_SUP) {
        return {
          setSnackInfo,
          min: 50,
          max: 99.99,
          point: true
        };
      }
    }

    return { valueFormatter: "", values: [] }
  }


  const editable = (params) => {
    const { invPolicyCd } = params.data;

    if (params.node && params.node.rowPinned === 'bottom') {
      return false;
    }

    if (isNotReleaseVersion({ data: selectedScenario })) {
      return invPolicyCd !== INV_POLICY_TYPE.MAX_AVG;
    }

    return false;
  }

  const defaultEditable = (params) => {
    if (params.node && params.node.rowPinned === 'bottom') {
      return false;
    }

    return isNotReleaseVersion({ data: selectedScenario });
  }

  const currencyRenderer = param => {
    const locCd = param?.data?.location?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      } 
    }
    
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };


  const targetInventoryPolicyColumnDefs = [
    { headerName: "locCd", field: "location.locCd", type: ["nonEditableColumn"], width: 140, rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "locName", field: "location.name", type: ["nonEditableColumn"], width: 140, rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "locType", field: "location.locType", type: ["nonEditableColumn"], width: 140, rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "invClassCd", field: "invClassCd", type: ["nonEditableColumn"], cellRenderer: AbcXyzChip, width: 130, filter: 'agTextColumnFilter' },
    {
      headerName: "targetStock", groupId: "targetStock",
      children: [
        { headerName: "safetyStock", field: "safetyStockFlg", type: ["booleanColumn"], width: 130, filter: 'agTextColumnFilter', editable: isNotReleaseVersion({ data: selectedScenario }) },
        { headerName: "cycleStock", field: "cycleInvFlg", type: ["booleanColumn"], width: 130, filter: 'agTextColumnFilter', editable: isNotReleaseVersion({ data: selectedScenario }) }
      ]
    },
    {
      headerName: "demandRate", groupId: "demandRate",
      children: [
        { 
          headerName: "salesFlg", field: "salesFlg", type: ["booleanColumn"], width: 110, filter: 'agTextColumnFilter', 
          editable: (params) => salesDemandTypeEditableHandler(selectedScenario, params),
          cellStyle: (params) => plantLocEditableCellStyle(selectedScenario, params)
        },
        { 
          headerName: "shipmentsFlg", field: "shipmentsFlg", type: ["booleanColumn"], width: 110, filter: 'agTextColumnFilter', 
          editable: forecastGapEditableHandler({ data: selectedScenario }),
          cellStyle: (e) => forecastGapCellStyle({ data: selectedScenario }, e)
        }
      ]
    },
    {
      headerName: "invPolicyCd", field: "invPolicyCd", cellEditor: "agRichSelectCellEditor", filter: 'agTextColumnFilter', cellEditorPopup: true, width: 170, editable: defaultEditable,
      valueFormatter, cellEditorParams: { valueFormatter: params => t(params.value), values: invPolicyTypes }
    },
    {
      headerName: "invPolicyVal", field: "invPolicyVal", width: 170, type: ["rightAligned"], editable,
      cellStyle: cellDynamicStyle, cellRenderer: invPolicyValCellRenderer, cellEditorSelector, filter: 'agNumberColumnFilter'
    },
    {
      headerName: "invAmt", groupId: "invAmt",
      children: [
        {
          headerName: "onhand", field: "currentInvAmt",
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          filter: 'agNumberColumnFilter',
          cellRenderer: currencyRenderer,
          width: 180
        },
        {
          headerName: "targetStock", field: "targetInvAmt",
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          filter: 'agNumberColumnFilter',
          cellRenderer: currencyRenderer,
          width: 180
        },
        {
          headerName: "monthlyAvgStock", field: "avgInvAmt",
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          filter: 'agNumberColumnFilter',
          cellRenderer: currencyRenderer,
          width: 180, cellStyle: cellDynamicStyle
        },
        {
          headerName: "gap", field: "invGapAmt",
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          filter: 'agNumberColumnFilter',
          cellRenderer: currencyRenderer,
          width: 180, cellStyle: cellDynamicStyle
        }
      ]
    }
  ];

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 180,
      filter: 'agTextColumnFilter',
      filterValueGetter: (params) => {
        const colId = params.column.getColId();
        if (colId.includes('locCd')) {
          return params.data.location.locCd;
        } else if (colId.includes('name')) {
          return params.data.location.name;
        } else if (colId.includes('locType')) {
          return params.data.location.locType;
        }
        return false;
      },
      pinned: 'left'
    };
  }, []);

  const policyGridRowStyles = (params) => {
    const styles = {};
    const selectedScenarioStatus = selectedScenario?.statusCd;

    if (params.node && params.node.rowPinned === 'bottom') {
      styles.editable = false;
    }

    if (isEqual(selectedScenarioStatus, RELEASED)) {
      styles.backgroundColor = grey[300];
    }

    return styles;
  };

  const retrieveScenarios = () => {
    const params = getRequestParams();
    if (searchScenarioName.current) {
      params.name = searchScenarioName.current;
    }

    targetInvSimulationService.getAll(params)
      .then(response => {
        const { results, allPlanResults } = response.data;
        if (isEmpty(results)) {
          setSelectedScenario([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          setScenarios([]);
          setOrgScenarios([]);
        } else {
          setScenarios(results);
          setOrgScenarios(allPlanResults);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        DataState.initialize(scenarioGridRef.current?.api);

        const searchCodeState = reduxUtil.createViewState(viewName, searchScenarioName.current);
        reduxDispatch(reduxUtil.updateViewState(searchCodeState));
      })
  };

  const reload = (value) => {
    if (value) {
      retrieveScenarios();
      reloadIpVersionFilter(reduxDispatch);
    }
  };

  const retrieveTargetInventoryPolicy = (id) => {
    targetInventoryPolicyGridRef.current?.api?.setGridOption('loading', true);

    targetInvSimulationService.getPolicy({ ipVersionId: id, nowDate: new ZDate() })
      .then((response) => {
        const { data, currency, total } = response.data;
        setTargetInventoryPolicy(data);
        setTotalRowData(total);
        if (size(currency)) {
          currencyMap.current = currency;
        }

      }).catch(e => {
        const message = e.response?.data ? e.response.data : "MSG_NetworkError";
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t(message) });
        debug(e);
      }).finally(() => {
        targetInventoryPolicyGridRef.current?.api?.setGridOption('loading', false);
      });
  };

  const runScenarioSimulation = () => {
    let errorMsg = "";
    if (isEmpty(selectedScenario)) {
      errorMsg = "MSG_NoScenario";
    } else if (isEmpty(selectedScenario.classificationDto)) {
      errorMsg = "MSG_NotAppliedClassifi";
    } else if (!(selectedScenario.classificationDto?.statusCd.toUpperCase() === READY || selectedScenario.classificationDto?.statusCd.toUpperCase() === PLANNED)) {
      errorMsg = "MSG_NotReadyClassifi";
    } else if (isEmpty(selectedScenario.policyObj)) {
      errorMsg = "MSG_NotAppliedInvPolicy";
    }

    if (!errorMsg) {
      setIsRunning(true);

      targetInventoryPolicyGridRef.current?.api?.setGridOption('loading', true);
      targetInvSimulationService.run({...selectedScenario, nowDate: new ZDate()})
        .then((response) => {
          const { data } = response;

          let severity = snackBarMessageColor.saveSuccess;
          if (!isEmpty(data?.message) && data.message === "MSG_NoData") {
              severity = snackBarMessageColor.noSaveData;
          }

          setSnackInfo({ open: true, severity, content: t(data?.message) });
        })
        .catch(e => {
          const { status, data } = e.response;
          if (status === 400) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t(data?.message) });
          } else if (status === 409) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(data?.message, { target: selectedScenario.name }) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(data?.message) });
          }

          debug(e);
        })
        .finally(() => {
          retrieveIpVersions();
          setIsRunning(false);
          reload(true);
          targetInventoryPolicyGridRef.current?.api?.setGridOption('loading', false);
        })
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(errorMsg) });
    }
  }

  const saveDataUniqueKeyValidation = (data, allStateData) => {
    const uniqueKeyArray = ["name"];
    const allStateDataKeyArray = allStateData.map((allState) => { return allState.id });

    if (isEmpty(data.classificationDto)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotAppliedClassifi") });
      return false;
    }

    for (let i = 0, arraySize = orgScenarios.length; i < arraySize; i++) {
      const originData = orgScenarios[i];
      if (allStateDataKeyArray.indexOf(originData.id) === -1) {
        for (const uniqueKey of uniqueKeyArray) {
          if (data[uniqueKey] === originData[uniqueKey]) {
            let columnHeader = uniqueKey;

            scenariosGridColDefs.forEach((col) => {
              if (col?.field === uniqueKey) { // unique key column 
                columnHeader = col?.headerName ? col.headerName : col.field;
              }
            });
            setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `Duplicate Column is [${t(columnHeader)}], value [${data[uniqueKey]}]` });

            return false;
          }
        }
      }
    }

    for (let i = 0, arraySize = allStateData.length; i < arraySize; i++) {
      const originData = allStateData[i];
      if (data.id !== originData.id) {
        for (const uniqueKey of uniqueKeyArray) {
          if (data[uniqueKey] === originData[uniqueKey]) {
            let columnHeader = uniqueKey;

            scenariosGridColDefs.forEach((col) => {
              if (col?.field === uniqueKey) { // unique key column 
                columnHeader = col?.headerName ? col.headerName : col.field;
              }
            });
            setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `Duplicate Column is [${t(columnHeader)}], value [${data[uniqueKey]}]` });

            return false;
          }
        }
      }
    }

    return true;
  }

  const setKeepResultsValue = (data) => {
    orgScenarios.forEach(orgScenario => {
      if (isEqual(data.id, orgScenario.id)) {
        /* Policy 및 Classification 정보가 변하지 않는다면 결과값 유지 */
        if (isEqual(data.policyObj, orgScenario.policyObj) && isEqual(data.classificationDto, orgScenario.classificationDto)) {
          data.keepResults = true;
        }

        if (!isEqual(data.classificationDto?.plantLocAnalysis, orgScenario.classificationDto?.plantLocAnalysis)) {
          /* 등급분류의 대상값이 다르다면 (DC != PLANT) Policy 정책 초기화 */
          data.changedClassification = true;
        }

        /* 구간 값이 변하는경우 결과값을 유지 하지 않음 */
        if (!isEqual(data?.actualPeriodEndDate, orgScenario?.actualPeriodEndDate)) {
          data.keepResults = false;
        } else if (!isEqual(data?.actualPeriodVal, orgScenario?.actualPeriodVal)) {
          data.keepResults = false;
        } else if (!isEqual(data?.demandPeriodStartDate, orgScenario?.demandPeriodStartDate)) {
          data.keepResults = false;
        } else if (!isEqual(data?.demandPeriodVal, orgScenario?.demandPeriodVal)) {
          data.keepResults = false;
        }
      }
    });
  };

  const setEssentialPeriodData = (datas) => {
    const essentialDataKeyArray = ["actualPeriodEndDate", "demandPeriodStartDate"];

    for (const data of datas) {
      for (const essentialDataKey of essentialDataKeyArray) {
        if (isEmpty(data?.[essentialDataKey])) {
          const initDate = new ZDate();
          if(essentialDataKey === "actualPeriodEndDate") {
            initDate.setMonth(initDate.getMonth());
            initDate.setDate(0);
            initDate.setHours(0);
            initDate.setMinutes(0);
            initDate.setSeconds(0);
          } else {
            initDate.setDate(1);
            initDate.setHours(0);
            initDate.setMinutes(0);
            initDate.setSeconds(0);
          }

          data[essentialDataKey] = initDate;
        }
      }
    }

    return true;
  }

  const handleSave = (saveData) => {
    const allStateData = DataState.getAllStateData(scenarioGridRef.current.api);
    
    if (!isNil(saveData)) {
      // saveData.id 가 존재할 경우 동일 id 객체 탐색 후 policyObj 만 교체, 없으면 push
      if (!isNil(saveData.id)) {
        const idx = allStateData.findIndex(d => d && isEqual(d.id, saveData.id));
        if (idx > -1) {
          // policyObj[SIM_TARGET_FILTER_POLICY] 만 교체 (기존 객체의 다른 변경사항은 유지)

          allStateData[idx] = { ...allStateData[idx], policyObj: {...allStateData[idx].policyObj, [SIM_TARGET_FILTER_POLICY]: saveData.policyObj[SIM_TARGET_FILTER_POLICY]} };
        } else {
          allStateData.push(saveData);
        }
      } else {
        // id 없으면 신규 데이터로 간주하여 push
        allStateData.push(saveData);
      }
    }

    if (allStateData && allStateData.length > 0) {
      let dataValidate = true;

      allStateData.forEach((data) => {
        if (!saveDataUniqueKeyValidation(data, allStateData)) { // unique key check for all org Scenarios, update Scenarios
          dataValidate = false;
        }

        if (isEqual(data?.actualPeriodVal, 0) && isEqual(data?.demandPeriodVal, 0)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `${t("numOfMonths")} ${t("MSG_ValueGreater")} ${0}` });
          dataValidate = false;
        }

        if (data.id) { // set keepResults for data with only name and description modified
          setKeepResultsValue(data);
        }
      });

      /* 구간 값이 빠져있는경우 기본 설정. */
      setEssentialPeriodData(allStateData);

      if (dataValidate) {
        targetInvSimulationService.upsert(allStateData)
          .then(() => {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          })
          .catch(e => {
            const { status } = e.response;
            if (status === 409) {
              const targetScenarios = allStateData.map(data => data.name).join(", ");
              setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: t("MSG_OptimisticLockAboutTarget", { target: `[${targetScenarios}]` }) });
            } else {
              setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
            }

            debug(e);
          })
          .finally(() => {
            const stateRows = targetInventoryPolicyGridRef.current.api.stateRows;
            if (stateRows && (stateRows.created.length > 0 || stateRows.updated.length > 0)) {
              const policyAllStateData = DataState.getAllStateData(targetInventoryPolicyGridRef.current.api);

              targetInvSimulationService.upsertPolicy(policyAllStateData)
                .then(() => {
                  setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
                })
                .catch(e => {
                  setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
                  debug(e);
                })
                .finally(() => {
                  reload(true);
                })
            } else {
              reload(true);
            }
          });
      }
    } else if (targetInventoryPolicyGridRef.current.api.stateRows) { // save only Target Inv Policy
      const policyAllStateData = DataState.getAllStateData(targetInventoryPolicyGridRef.current.api);

      targetInvSimulationService.upsertPolicy(policyAllStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          retrieveScenarios();
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        });
    }
  }

  const removeRow = ({ selectRow, selectedNodes }) => {
    if (selectRow.id === null) {
      DataState.deleteState(scenarioGridRef.current.api, selectedNodes);
    } else {
      targetInvSimulationService.remove(selectRow.id)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
          retrieveScenarios();
          retrieveIpVersions();
        })
        .catch((e) => {
          const { data } = e.response;
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(data) });
          debug(e);
        }).then(() => {
          DataState.initialize(targetInventoryPolicyGridRef.current.api);
        })
    }
  }

  const onRowSelected = params => {
    const selectedRows = params.api.getSelectedRows();
    const selectedRow = selectedRows.length > 0 ? selectedRows[0] : {};
    setSelectedScenario(selectedRow);

    if (isEqual(selectedRow?.classificationDto?.xyzCalcType, FORECAST_GAP)) {
      // remove shipmentsFlg in update Targets Array
      setUpdateTargets(["safetyStockFlg", "cycleInvFlg", "salesFlg", "invPolicyCd", "invPolicyVal"]);
    }

    // 복원은 id 만 쓴다. name 은 Agent Chat 이 화면 조건(선택 시나리오)으로 읽는다.
    const reduxSelectedRowData = { id: selectedRow?.id, name: selectedRow?.name };
    const reduxState = reduxUtil.createObjectState(viewName, { scenario: reduxSelectedRowData });
    reduxDispatch(reduxUtil.updateViewState(reduxState));

    DataState.initialize(targetInventoryPolicyGridRef.current?.api);
  }

  const onCellValueChanged = params => {
    const { column, oldValue, newValue, node } = params;
    const editedColId = column.getColId();

    if (editedColId === "classificationDto") {
      /* Classification 변경시 하단 그리드의 Re-Redering  */
      if (isEqual(oldValue?.xyzCalcType, FORECAST_GAP) || isEqual(newValue?.xyzCalcType, FORECAST_GAP)) {
        if (isEqual(newValue?.xyzCalcType, FORECAST_GAP)) {
          node.setDataValue("demandPeriodVal", 0);
        }

        setSelectedScenario(prev => ({ ...prev, classificationDto: newValue }));
      }

      if (oldValue.name !== newValue.name) {
        DefaultGridSetting({}).onCellValueChanged(params);
      }
    } else {
      DefaultGridSetting({}).onCellValueChanged(params);
    }
  }

  const onGridReady = (params, gridId) => {
    setGridLoading(true);

    if(gridId === SCENARIO_GRID_ID) {
      GridUtils.setColumnDefs({ ...params, columnDefs: scenariosGridColDefs, viewName, gridId: SCENARIO_GRID_ID, initState: true });
    } else if(gridId === TARGET_INV_POLICY_GRID_ID) {
      GridUtils.setColumnDefs({ ...params, columnDefs: targetInventoryPolicyColumnDefs, viewName, gridId: TARGET_INV_POLICY_GRID_ID, initState: true });
    }
  };

  const onTargetInvPolicyCellValueChanged = params => {
    const { column, node } = params;
    const editedColId = column.getColId();

    if (editedColId === "invPolicyCd") {
      node.setDataValue("invPolicyVal", 0);
    }

    DefaultGridSetting({}).onCellValueChanged(params);
  }

  const handleModalOpen = (modalName) => {
    if (modalName === "CREATE") {
      setCreateNewModalOpen(true);
    }
  }

  const handleDialogOpen = async (action) => {
    scenarioGridRef.current.api.stopEditing(false);

    if (action === DIALOG_TITLE.SAVE) {
      const allStateData = DataState.getAllStateData(scenarioGridRef.current.api);
      let policyAllStateData = [];

      if (targetInventoryPolicyGridRef.current.api.stateRows) {
        policyAllStateData = DataState.getAllStateData(targetInventoryPolicyGridRef.current.api);
      }

      if (allStateData.length > 0 || policyAllStateData.length > 0) {
        setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      }

    } else if (action === DIALOG_TITLE.DELETE) {
      const selectedNodes = scenarioGridRef.current.api.getSelectedNodes();
      const selectRow = selectedNodes[0]?.data;

      if (selectRow) {
        setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm"), el: { selectedNodes, selectRow } });
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
      }
    } else if (action === DIALOG_TITLE.RUN) {
      const selectedNodes = scenarioGridRef.current.api.getSelectedNodes();
      const selectRow = selectedNodes[0]?.data;

      if (selectRow) {
        /* Message: [MSG_MonthlyTransformRequired, MSG_MonthlyTransformUpdateRequired] */
        const monthlyPSICheckMessage = await appConfigService.asyncCheckMonthlyPSIData();

        if (monthlyPSICheckMessage === "MSG_MonthlyTransformUpdateRequired") {
          setDialogInfo({ open: true, title: "runSimulation", actionName: "RUN_SIMULATION", content: `${t("MSG_RunSimulConfirm")}\n${t(monthlyPSICheckMessage)}` });
        } else if (monthlyPSICheckMessage === "MSG_MonthlyTransformRequired") {
          setDialogInfo({ open: true, title: "info", actionName: "", content: t(monthlyPSICheckMessage) });
        } else {
          setDialogInfo({ open: true, title: "runSimulation", actionName: "RUN_SIMULATION", content: t("MSG_RunSimulConfirm") });
        }
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
      }
    }
  }

  const handleDialog = (action, _falseAction, el) => {
    switch (action) {
      case DIALOG_TITLE.SAVE: {
        handleSave();
        break;
      }
      case DIALOG_TITLE.DELETE: {
        removeRow(el);
        break;
      }
      case DIALOG_TITLE.RUN: {
        runScenarioSimulation();
        break;
      }
      default:
        break;
    }
  }

  useEffect(() => {
    selectedRowRef.current = selectedScenario;
    if (Object.keys(selectedScenario).length !== 0) {
      retrieveTargetInventoryPolicy(selectedScenario.id);
    } else {
      setTargetInventoryPolicy([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScenario])

  useEffect(() => {
    if (gridLoading) {
      retrieveIpVersions();
      retrieveClassificationList();
      retrieveScenarios();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading]);

  useEffect(() => {
    if (!isEmpty(classificationList)) {
      GridUtils.setColumnDefs({ ...scenarioGridRef.current, columnDefs: scenariosGridColDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classificationList]);

  useEffect(() => {
    if (!isEmpty(targetInventoryPolicy)) {
      GridUtils.setColumnDefs({ ...targetInventoryPolicyGridRef.current, columnDefs: targetInventoryPolicyColumnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetInventoryPolicy]);

  useEffect(() => {
    if (gridLoading && scenarios?.length > 0) {
      const api = scenarioGridRef.current.api;
      if (reduxViewState?.scenario?.id) {
        let hasEquals = false;
        api.forEachNode(node => {
          if (isEqual(node.data?.id, reduxViewState?.scenario.id)) {
            node.setSelected(true);
            hasEquals = true;
          }
        });

        if (!hasEquals) {
          api.getDisplayedRowAtIndex(0)?.setSelected(true);
        }
      } else {
        api.getDisplayedRowAtIndex(0)?.setSelected(true);
      }

      GridUtils.focusOnSelectedRow(api);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedVersionIsRunning = !isEmpty(selectedScenario) && !isNull(selectedScenario) && isEqual(selectedScenario?.statusCd, RUNNING);
  return (
    <>
      <FilterContainer>
        <TextFieldSearch sx={{ midWidth: 240 }} label={"scenario"} searchCode={searchScenarioName} retrieve={retrieveScenarios} />
        <Box sx={{ ml: "auto" }}>
          {/* 결과 화면이 권한 밖인 테넌트에서는 버튼 자체를 내보내지 않는다 */}
          {isPermitted("TARGET_INV_SIMULATION_RESULTS") && (
            <ResultButton disabled={isEmpty(selectedScenario) || isNull(selectedScenario) || ![RELEASED, READY, PLANNED].includes(selectedScenario?.statusCd)}
              onClick={() => showResultsPage()} />
          )}
          <CreateButton onClick={() => handleModalOpen("CREATE")} />
          <RemoveButton onClick={() => handleDialogOpen(DIALOG_TITLE.DELETE)} disabled={selectedVersionIsRunning} />
          <SaveButton onClick={() => handleDialogOpen(DIALOG_TITLE.SAVE)} disabled={selectedVersionIsRunning} />
          {(isRunning || selectedVersionIsRunning) ? <RunningButton /> : <RunButton label="runSimulation" disabled={isEqual(selectedScenario?.statusCd, RELEASED)} onClick={() => handleDialogOpen(DIALOG_TITLE.RUN)} />}
        </Box>
      </FilterContainer>
      <Box className="grid-area" sx={{ display: "flex", flexDirection: "column" }}>
        {/* TOP Area */}
        <Box className="ag-theme-balham" sx={{ width: "100%", minHeight: "200px", height: "40%" }}>
          <AgGridReact
            ref={scenarioGridRef}
            rowData={scenarios}
            {...defaultMainGridMemo}
            rowHeight={34}
            rowSelection={{ mode: "singleRow", checkboxes: true, headerCheckbox: false, enableClickSelection: true }}
            selectionColumnDef={{ pinned: 'left', width: 40 }}
            onRowSelected={onRowSelected}
            onGridReady={(e) => onGridReady(e, SCENARIO_GRID_ID)}
            onCellValueChanged={onCellValueChanged}
          />
        </Box>

        {/* BOTTOM Area */}
        <Box className="ag-theme-balham" py={1} style={{ width: "100%", minWidth: "auto", height: "calc(60% - 25px)" }}>
          <Box sx={{ display: "flex", height: "30px", alignItems: "flex-end", justifyContent: "left" }}>
            <Typography variant="h5" sx={{ pl: 1, textAlign: "center", fontWeight: 500, fontSize: FS.DISPLAY, mb: 1 }}>
              {t("inventoryPolicyByLocation")}
            </Typography>
            <Box sx={{ ml: "auto", mr: 1.5 }}>
              <BulkValueUpdateButton onClick={() => setMassUpdateModalOpen(true)} disabled={isEqual(selectedScenario?.statusCd, RELEASED)} />
            </Box>
          </Box>
          <AgGridReact
            ref={targetInventoryPolicyGridRef}
            rowData={targetInventoryPolicy}
            {...defaultPolicyGridMemo}
            autoGroupColumnDef={autoGroupColumnDef}
            groupHideOpenParents
            groupDefaultExpanded={-1}
            getRowStyle={policyGridRowStyles}
            pinnedBottomRowData={[totalRowData]}
            onCellValueChanged={onTargetInvPolicyCellValueChanged}
            onGridReady={(e) => onGridReady(e, TARGET_INV_POLICY_GRID_ID)}
            statusBar={statusBar}
          />
        </Box>
      </Box>
      <IpBulkValueUpdateModal
        t={t}
        updateTargets={updateTargets}
        service={targetInvSimulationService}
        modalOpen={massUpdateModalOpen}
        setModalOpen={setMassUpdateModalOpen}
        selectedScenario={selectedScenario}
        setSnackInfo={setSnackInfo}
        setProgressOn={setIsRunning}
        retrieve={retrieveScenarios}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        el={dialogInfo.el}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <CreateScenarioModal
        viewName={viewName}
        t={t}
        modalOpen={createNewModalOpen}
        planVersions={planVersions}
        retrievePlanVersion={retrieveIpVersions}
        iconColor={iconColor}
        setModalOpen={setCreateNewModalOpen}
        reload={reload}
        setSnackInfo={setSnackInfo}
        classificationList={classificationList}
      />
      <TargetSimulationModal
        t={t}
        viewName={viewName}
        modalSelectedScenario={modalSelectedScenario}
        handleSave={handleSave}
        modalOpen={targetSimulationModalOpen}
        handleClose={setTargetSimulationModalOpen}
        setSnackInfo={setSnackInfo}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        simTargetFilterPolicy={simTargetFilterPolicy}
        setSimTargetFilterPolicy={setSimTargetFilterPolicy}
      />
    </>
  );
}

const TargetSimulationModal =({t, viewName, setSnackInfo, modalSelectedScenario, handleSave, modalOpen, handleClose, filterValue, setFilterValue, simTargetFilterPolicy, setSimTargetFilterPolicy}) => {
  const isReleased = isEqual(modalSelectedScenario?.statusCd, RELEASED);

  const modalCloseHandler = () => {
    setFilterValue({ locations: [], category: [] });
    setSimTargetFilterPolicy({ [SIM_TARGET_FILTER_ENABLED]: false, [TARGET_LOCATION]: [], [LOCATION_NODE_TREE]: [], [TARGET_ITEM_HRCHY]: [], [ITEM_HRCHY_NODE_TREE]: [] });

    handleClose(false);
  }

  const saveHandler = (isSave) => {
    if (isSave && !isReleased) {
      const saveNode = { ...modalSelectedScenario };
      if (!isNil(saveNode?.policyObj)) {
        saveNode.policyObj[SIM_TARGET_FILTER_POLICY] = simTargetFilterPolicy;
      } else {
        saveNode.policyObj = { [SIM_TARGET_FILTER_POLICY]: simTargetFilterPolicy };
      }

      if (simTargetFilterPolicy?.[SIM_TARGET_FILTER_ENABLED]) {
        if(isEmpty(simTargetFilterPolicy?.[TARGET_LOCATION])) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoSelectedSimTargetLocation") });
          return;
        }

        if(isEmpty(simTargetFilterPolicy?.[TARGET_ITEM_HRCHY])) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoSelectedSimTargetItemHrchy") });
          return;
        }
      }

      handleSave(saveNode);
    }

    modalCloseHandler();
  }

  useEffect(() => {
    if (modalOpen && !isNil(modalSelectedScenario) && !isNil(modalSelectedScenario?.policyObj)) {
      const policyObj = modalSelectedScenario?.policyObj;
      const selectedSimTargetPolicy = policyObj?.[SIM_TARGET_FILTER_POLICY] || {[SIM_TARGET_FILTER_ENABLED]: false, [TARGET_LOCATION]: [], [LOCATION_NODE_TREE]: [], [TARGET_ITEM_HRCHY]: [], [ITEM_HRCHY_NODE_TREE]: []};
      const selectedTargetLocTree = selectedSimTargetPolicy?.[LOCATION_NODE_TREE] || [];
      const selectedTargetItemHrchyTree = selectedSimTargetPolicy?.[ITEM_HRCHY_NODE_TREE] || [];

      setSimTargetFilterPolicy(selectedSimTargetPolicy);
      setFilterValue({ locations: selectedTargetLocTree, category: selectedTargetItemHrchyTree });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalSelectedScenario, modalOpen]);

  return (
    /* isReleased 면 onSave 를 주지 않는다 — 셸이 저장 버튼을 통째로 감춘다.
       원래는 <Button hidden={isReleased}> 였는데 동작하지 않았다: [hidden]{display:none} 은 UA 스타일이라
       MUI 의 .MuiButton-root{display:inline-flex}(작성자 스타일)에 진다. 죽은 버튼이 그대로 보였다. */
    <BaseConfigModal
      open={modalOpen}
      onClose={modalCloseHandler}
      title={t("advancedSettings")}
      titleAdornment={
        <IpTargetLocationItemHrchyEnabledComponent
          simTargetFilterPolicy={simTargetFilterPolicy}
          setSimTargetFilterPolicy={setSimTargetFilterPolicy}
          disabled={isReleased}
          sx={{ mb: 0 }}
          switchSx={{ mr: -2 }}
        />
      }
      onSave={isReleased ? undefined : () => saveHandler(true)}
      width={642}>
        <Box width={"100%"}>
          <IpTargetLocationItemHrchyComponentContent
            t={t}
            viewName={viewName}
            simTargetFilterPolicy={simTargetFilterPolicy}
            setSimTargetFilterPolicy={setSimTargetFilterPolicy}
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            contentStyles={{ display: "flex", mt: 2}}
            disabled={isReleased}
          />
        </Box>
    </BaseConfigModal>
  )
}

export default withTranslation()(TargetInvSimulation);
